import { ACCOUNT_MANAGEMENT_LE_ADMIN, WORKSPACE_ORG_ADMIN } from '../../../constants/roles';

import type { EntityRole } from './constants';
import type { WorkspaceTeamMember } from './workspaceTeam';

/** The relevant slice of the current user's `WorkspaceTeamMember`, from useWorkspaceTeam `currentMember`. */
type WorkspaceMemberAccess = Pick<
  WorkspaceTeamMember,
  'orgRoles' | 'effectiveRoles' | 'pendingRoles'
>;

/**
 * Whether the current user holds an LE admin role on `legalEntityId` (any entity
 * when omitted). An effective admin role always counts. A pending one counts
 * once its `effectiveAt` has passed, or always without `now` (control). With
 * `now` (IDFE-4022, t0018_4fba688a treatment), an admin role still in its grace
 * period doesn't count.
 */
function hasAdminRole(
  { effectiveRoles, pendingRoles }: WorkspaceMemberAccess,
  now: Date | undefined,
  legalEntityId?: string,
): boolean {
  const isAdminOnEntity = ({ role, legalEntityId: id }: EntityRole) =>
    role === ACCOUNT_MANAGEMENT_LE_ADMIN && (legalEntityId == null || id === legalEntityId);

  return (
    effectiveRoles.some(isAdminOnEntity) ||
    pendingRoles.some(
      (pendingRole) =>
        isAdminOnEntity(pendingRole) && (now == null || pendingRole.effectiveAt <= now),
    )
  );
}

/**
 * Returns true if the current user may manage members on `legalEntityId`.
 * The trays list every entity the user can see (ACM viewer or admin), but only
 * an admin on that specific entity — or a workspace_org_admin, which spans the
 * whole org — may change roles there.
 *
 * Pass `now` (from useManagePermissionsNow) to ignore an admin role still in its
 * grace period; omit it for today's behavior.
 */
export function canManageEntity(
  currentUser: WorkspaceMemberAccess | undefined,
  legalEntityId: string,
  now?: Date,
): boolean {
  if (currentUser == null) return false;
  return (
    currentUser.orgRoles.includes(WORKSPACE_ORG_ADMIN) ||
    hasAdminRole(currentUser, now, legalEntityId)
  );
}

/**
 * Returns true if the current user may manage members (invite or edit roles).
 * Invite and edit share the exact same permission. Requires either:
 * - account_management/legal_entity:admin on at least one legal entity, or
 * - workspace_org_admin (org-level, in `orgRoles`).
 *
 * Pass `now` (from useManagePermissionsNow) to ignore admin roles still in their
 * grace period; omit it for today's behavior.
 */
export function canManageMembers(
  currentUser: WorkspaceMemberAccess | undefined,
  now?: Date,
): boolean {
  if (currentUser == null) return false;
  return currentUser.orgRoles.includes(WORKSPACE_ORG_ADMIN) || hasAdminRole(currentUser, now);
}

/**
 * Returns true if the current user may remove a member from the organization.
 * Stricter than managing members: requires the org-level workspace_org_admin role.
 */
export function canRemoveMembers(
  currentUser: Pick<WorkspaceTeamMember, 'orgRoles'> | undefined,
): boolean {
  return currentUser?.orgRoles.includes(WORKSPACE_ORG_ADMIN) ?? false;
}
